import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { Spin } from 'antd';
import { usePermissions } from '../../hooks/usePermissions';

interface PermissionRouteProps {
  menuCode: string;
  children?: React.ReactNode;
}

/**
 * Route wrapper that checks the current user has the `get` (view) bit for the given menu code.
 *
 * - While permissions are loading → shows a centred spinner (avoids flash of "access denied")
 * - `get` bit not set → redirects to /dashboard
 * - `get` bit set → renders children or <Outlet />
 *
 * /dashboard itself is NOT wrapped with this component — it is always accessible.
 */
const PermissionRoute: React.FC<PermissionRouteProps> = ({ menuCode, children }) => {
  const { hasPermission, isLoading } = usePermissions();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!hasPermission(menuCode, 'get')) {
    return <Navigate to="/dashboard" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export default PermissionRoute;
